import React, { createContext, useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import PageHeaderLayout from '../../../../components/Layout/PageHeaderLayout';
import { Box, Button, Card, Grid, Typography } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import Swal from 'sweetalert2';
// import SectionServices from '../../../../services/CustomerApp/HomeScreen/SectionServices';
import dayjs from 'dayjs';
import { SectionServices } from './SectionServices';
import SectionForm from './SectionForm';
import MobilePreview, { InlineSectionPreview } from './MobilePreview';
import Loading from '../../../../components/Loader/Loading';
import { stringifyNumericDisplayOptions } from '../displayOptions';

export const SectionDataContext = createContext();

const AddEditSection = () => {
    const menuDispatch = useDispatch();
    const {id} = useParams();
    const [searchParams] = useSearchParams();
    const defaultPageType = searchParams.get('pageType') || 'section';
    useEffect(() => {
        menuDispatch({
            type: 'menuSelect',
            payload: defaultPageType === 'header' ? 'header-management' : defaultPageType === 'footer' ? 'footer-management' : 'sections'
        });
    }, [defaultPageType, menuDispatch]);
    const isFooterPageType = defaultPageType === 'footer';
    const entityLabel = defaultPageType === 'header' ? 'Header' : defaultPageType === 'footer' ? 'Footer' : 'Section';
    const listPath = defaultPageType === 'header'
        ? '/customer-app/header-management'
        : defaultPageType === 'footer'
            ? '/customer-app/footer-management'
            : '/customer-app/sections';
    const navigate = useNavigate();
    const [pageTitle, setPageTitle] = useState(`Add ${entityLabel}`);
    const initialState = {
        title: '',
        subtitle: '',
        description: '',
        displayOptions: {
            showTitle: true,
            titlePosition: 'left',
            titleColor: '#000000',
            titleFontFamily: '',
            titleFontSize: '',
            titleFontWeight: '',
            titleTextAlign: 'left',
            showSubTitle: true,
            subTitlePosition: 'left',
            subTitleColor: '#000000',
            subTitleFontFamily: '',
            subTitleFontSize: '',
            subTitleFontWeight: '',
            subTitleTextAlign: 'left',
            enableSubTitleBackgroundColor: false,
            subTitleBackgroundColor: '#FFFFFF',
            showDescription: false,
            descriptionPosition: 'left',
            descriptionColor: '#000000',
            descriptionFontFamily: '',
            descriptionFontSize: '',
            descriptionFontWeight: '',
            descriptionTextAlign: 'left',
            customFontName: '',
            customFontUrl: null,
            headingSubtextSpacing: '',
            viewAllFontSize: '',
            viewAllFontWeight: '',
            bookAgainTimeGap: '',
            aspectRatio: 'auto',
            cardWidth: '',
            cardHeight: '',
            sectionPadding: { top: '', bottom: '', left: '', right: '' },
            sectionMargin: { top: '', bottom: '', left: '', right: '' },
            gradientStartColor: '#667eea',
            gradientEndColor: '#764ba2',
            pageType: defaultPageType,
            requiresServiceMapping: true,
            isNotVisible: false,
            actionType: defaultPageType === 'footer' ? 'open_url' : 'none',
            actionData: {
                url: ''
            },
        },
        sectionType: defaultPageType === 'footer' ? 'fixed_banner' : '',
        position: '',
        backgroundType: defaultPageType === 'footer' ? 'image' : 'color',
        backgroundColor: '#FFFFFF',
        backgroundImage: null,
        desktopBackgroundImage: null,
        showViewAll: false,
        viewAllText: 'View All',
        viewAllRoute: '',
        isActive: true,
        startDateTime: null,
        endDateTime: null,
        autoScrollDuration: null,
        maxItemsToShow: 10,
    };

    const [sectionData, setSectionData] = useState(initialState);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const SectionFormMethod = useForm({
        mode: 'onChange',
        defaultValues: initialState
    });

    const { handleSubmit, reset, control } = SectionFormMethod;

    useEffect(() => {
        if (id>0) {
            setPageTitle(`Edit ${entityLabel}`);
            setIsLoading(true);
            const fetchSectionData = async () => {
                try {
                    const response = await SectionServices.getSectionById(id);
                    if (response.success == '1') {
                        const data = response.data;
                        const requiredData = {
                            title: data.title || '',
                            subtitle: data.subtitle || '',
                            description: data.description || '',
                            displayOptions: {
                                ...initialState.displayOptions,
                                ...(data.displayOptions || {}),
                                requiresServiceMapping: data.displayOptions?.requiresServiceMapping ?? data.requiresServiceMapping ?? true,
                                isNotVisible: data.displayOptions?.isNotVisible === true,
                                enableSubTitleBackgroundColor: data.displayOptions?.enableSubTitleBackgroundColor ?? !!(
                                    data.displayOptions?.subTitleBackgroundColor &&
                                    data.displayOptions.subTitleBackgroundColor !== 'none' &&
                                    data.displayOptions.subTitleBackgroundColor.toUpperCase() !== '#FFFFFF' &&
                                    data.displayOptions.subTitleBackgroundColor.toUpperCase() !== '#FFFFFFFF'
                                )
                            },
                            sectionType: data.sectionType || '',
                            position: data.position || '',
                            backgroundType: (() => {
                                if (data.backgroundType === 'image' && data.backgroundImage) {
                                    const url = data.backgroundImage.toLowerCase();
                                    if (url.endsWith('.gif')) return 'gif';
                                    if (url.endsWith('.mp4') || url.endsWith('.webm') || url.endsWith('.ogg') || url.endsWith('.mov')) return 'video';
                                }
                                return data.backgroundType || 'none';
                            })(),
                            backgroundColor: data.backgroundColor || '#FFFFFF',
                            backgroundImage: data.backgroundImage || null,
                            desktopBackgroundImage: data.desktopBackgroundImage || null,
                            showViewAll: data.showViewAll || false,
                            viewAllText: data.viewAllText || 'View All',
                            viewAllRoute: data.viewAllRoute || '',
                            isActive: data.isActive ?? true,
                            startDateTime: data.startDateTime ? dayjs(data.startDateTime).format('YYYY-MM-DDTHH:mm') : null,
                            endDateTime: data.endDateTime ? dayjs(data.endDateTime).format('YYYY-MM-DDTHH:mm') : null,
                            autoScrollDuration: data.autoScrollDuration || null,
                            maxItemsToShow: data.maxItemsToShow || 10,
                        };
                        setSectionData(data);
                        reset(requiredData, { isDirty: false });
                    }
                } catch (error) {
                    console.log("error", error);
                } finally {
                    setIsLoading(false);
                }
            };
            fetchSectionData();
        } else {
            setPageTitle(`Add ${entityLabel}`);
        }
    }, [id, entityLabel]);

    const onSubmit = async (data) => {
        setIsSubmitting(true);
        const startDate = data.startDateTime ? new Date(data.startDateTime) : null;
        const endDate = data.endDateTime ? new Date(data.endDateTime) : null;
        if (startDate && endDate && endDate < startDate) {
            setIsSubmitting(false);
            Swal.fire({
                icon: 'error',
                title: 'Invalid Date Range',
                text: 'End date/time must be after start date/time.'
            });
            return;
        }
        if (data.isActive !== false && endDate && endDate < new Date()) {
            setIsSubmitting(false);
            Swal.fire({
                icon: 'error',
                title: 'Invalid End Date',
                text: `End date/time cannot be in the past for an active ${entityLabel.toLowerCase()}. Please update or clear the end date/time.`
            });
            return;
        }

        const modifiedData = {
            title: data.title,
            subtitle: data.subtitle || '',
            description: data.description || '',
            displayOptions: {
                ...stringifyNumericDisplayOptions(data.displayOptions),
                ...(isFooterPageType ? {
                    pageType: 'footer',
                    actionType: 'open_url',
                    actionData: {
                        url: data.displayOptions?.actionData?.url || ''
                    }
                } : {})
            },
            sectionType: isFooterPageType ? 'fixed_banner' : data.sectionType,
            position: data.position ? parseInt(data.position) : 0,
            backgroundType: isFooterPageType ? 'image' : (data.backgroundType === 'gif' || data.backgroundType === 'video') ? 'image' : data.backgroundType,
            backgroundColor: isFooterPageType ? null : (data.backgroundType === 'color' || data.backgroundType === 'gradient') ? data.backgroundColor : null,
            backgroundImage: isFooterPageType ? data.backgroundImage : (data.backgroundType === 'image' || data.backgroundType === 'gif' || data.backgroundType === 'video') ? data.backgroundImage : null,
            desktopBackgroundImage: isFooterPageType ? data.desktopBackgroundImage : (data.backgroundType === 'image' || data.backgroundType === 'gif' || data.backgroundType === 'video') ? data.desktopBackgroundImage : null,
            showViewAll: isFooterPageType ? false : data.showViewAll,
            viewAllText: isFooterPageType ? '' : data.showViewAll ? data.viewAllText : '',
            viewAllRoute: isFooterPageType ? '' : data.showViewAll ? data.viewAllRoute : '',
            isActive: data.isActive,
            startDateTime: data.startDateTime ? new Date(data.startDateTime).toISOString() : null,
            endDateTime: data.endDateTime ? new Date(data.endDateTime).toISOString() : null,
            autoScrollDuration: !isFooterPageType && data.sectionType === 'carousel' ? data.autoScrollDuration : null,
            maxItemsToShow: isFooterPageType ? 1 : data.maxItemsToShow ? parseInt(data.maxItemsToShow) : 10,
        };

        try {
            if (id>0) {
                const updateResponse = await SectionServices.updateSection(id, modifiedData);
                if (updateResponse.success == '1') {
                    await Swal.fire({
                        icon: 'success',
                        title: 'Success',
                    text: `${entityLabel} updated successfully`,
                    });
                    navigate(`/customer-app/home-screen/section/view/${id}?pageType=${defaultPageType}`);
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: 'Failed',
                        text: updateResponse.message || `${entityLabel} update failed`,
                    });
                }
            } else {
                const createResponse = await SectionServices.addSection(modifiedData);
                if (createResponse.success == '1') {
                    const result = await Swal.fire({
                        icon: 'success',
                        title: 'Success',
                        text: `${entityLabel} created successfully`,
                        showCancelButton: true,
                        confirmButtonText: 'Add New',
                        cancelButtonText: 'Show List',
                        confirmButtonColor: 'green',
                        cancelButtonColor: '#5856d6',
                        allowOutsideClick: false,
                        allowEscapeKey: false,
                    });
                    if (result.isConfirmed) {
                        reset(initialState);
                        setSectionData(initialState);
                    }
                    if (result.dismiss === Swal.DismissReason.cancel) {
                        navigate(listPath);
                    }
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: 'Failed',
                        text: createResponse.message || `${entityLabel} creation failed`,
                    });
                }
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Failed',
                text: id ? `${entityLabel} update failed` : `${entityLabel} creation failed`,
                timer: 1500
            });
        } finally {
            setIsSubmitting(false);
        }
    };
    const backgroundImage = useWatch({ control, name: 'backgroundImage' });
    const desktopBackgroundImage = useWatch({ control, name: 'desktopBackgroundImage' });
    const backgroundType = useWatch({ control, name: 'backgroundType' });
    const [previewOpen, setPreviewOpen] = useState(false);
    const previewFormData = SectionFormMethod.watch();
  return (
    <>
        <SectionDataContext.Provider value={{ sectionData, setSectionData, isSubmitting, onSubmit }}>
            {(isLoading || isSubmitting) && <Loading />}
            <PageHeaderLayout headerTitle={pageTitle}>
            <Button sx={{textTransform:'none'}} size='small' variant='contained' startIcon={<ArrowBack/>} onClick={()=>navigate(listPath)}>
                Show {entityLabel}s
            </Button>
        </PageHeaderLayout>
            <FormProvider {...SectionFormMethod}>
                <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                    <Grid container spacing={2}>
                        <SectionForm pageType={defaultPageType} />
                        <Grid item xs={12} md={4}>
                            {(backgroundType === 'image' || backgroundType === 'gif' || backgroundType === 'video') && 
                            <Card sx={{ p: 2,mb:1 }}>
                                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                                    Mobile Background File
                                </Typography>
                                {backgroundImage ? (
                                    <Box sx={{ textAlign: 'center', border: '1px solid #eee', borderRadius: 1, p: 1 }}>
                                        {backgroundType === 'video' ? (
                                            <video
                                                src={backgroundImage}
                                                controls
                                                style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain' }}
                                            />
                                        ) : (
                                            <img
                                                src={backgroundImage}
                                                alt="Mobile Background"
                                                style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain' }}
                                            />
                                        )}
                                    </Box>
                                ) : (
                                    <Box sx={{ textAlign: 'center', py: 4, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                                        <Typography sx={{ fontSize: '0.75rem', color: '#999' }}>No file</Typography>
                                    </Box>
                                )}
                            </Card>}
                            {(backgroundType === 'image' || backgroundType === 'gif' || backgroundType === 'video') && 
                            <Card sx={{ p: 2,mb:1 }}>
                                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                                    Desktop Background File
                                </Typography>
                                {desktopBackgroundImage ? (
                                    <Box sx={{ textAlign: 'center', border: '1px solid #eee', borderRadius: 1, p: 1 }}>
                                        {backgroundType === 'video' ? (
                                            <video
                                                src={desktopBackgroundImage}
                                                controls
                                                style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain' }}
                                            />
                                        ) : (
                                            <img
                                                src={desktopBackgroundImage}
                                                alt="Desktop Background"
                                                style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain' }}
                                            />
                                        )}
                                    </Box>
                                ) : (
                                    <Box sx={{ textAlign: 'center', py: 4, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                                        <Typography sx={{ fontSize: '0.75rem', color: '#999' }}>No file</Typography>
                                    </Box>
                                )}
                            </Card>}
                            <Card sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Button type="submit" variant="contained" color="primary" fullWidth disabled={isSubmitting}>
                                    {id>0 ? `Update ${entityLabel}` : `Add ${entityLabel}`}
                                </Button>
                                <Button type="button" variant="outlined" color="secondary" fullWidth onClick={() => navigate(listPath)}>
                                    Cancel
                                </Button>
                            </Card>
                            <Card sx={{ p: 2, mt: 2, position: 'sticky', top: 20 }}>
                                <InlineSectionPreview formData={previewFormData} sectionId={id} />
                            </Card>
                        </Grid>
                    </Grid>
                </Box>
            </FormProvider>
        </SectionDataContext.Provider>
        <MobilePreview open={previewOpen} onClose={() => setPreviewOpen(false)} formData={previewFormData} sectionId={id} />
    </>
  )
}

export default AddEditSection
